/**
 * Instance-level config baked in by slothfulchat-web builds as
 * `window.__slothfulConfig` (see packages/web-app/instance-config.mjs in the
 * slothfulchat-web repo). Absent on upstream Electron/Tauri runtimes.
 */

/**
 * `SLOTHFUL_HIDE_PUBLIC_SUGGESTIONS`: the operator hid the community
 * suggestions ("Public Bots", "Public Channels") for the whole instance —
 * the New Chat entries, both dialogs, and their settings toggles.
 */
export function instanceHidesPublicSuggestions(): boolean {
  return (window as any).__slothfulConfig?.hidePublicSuggestions === true
}

/**
 * `SLOTHFUL_DEFAULT_CHATMAIL`: parse the operator-configured default chatmail
 * relay into the `dcaccount:` QR the instant-onboarding "create new account"
 * flow signs up with, plus a bare display host for the consent text.
 *
 * Accepts a bare host (`chat.example.org`), a full URL
 * (`https://chat.example.org/new`), or an already-formed `dcaccount:` QR:
 *  - `dcaccount:` values pass through untouched (core parses the scheme).
 *  - `http(s)://` URLs are kept whole as the payload — core POSTs to such
 *    account-creation endpoints, so the path/query must survive.
 *  - a bare host has any path/query/fragment stripped — core creates the
 *    address on first login against that domain (upstream's default form).
 *
 * Returns `undefined` when unset, blank, or degenerate (no host), so callers
 * fall back to upstream's default instance instead of a broken QR.
 */
function parseDefaultChatmail(): { qr: string; host: string } | undefined {
  const value = (window as any).__slothfulConfig?.defaultChatmailInstance
  if (typeof value !== 'string' || value.trim() === '') {
    return undefined
  }
  const trimmed = value.trim()
  // display host = whatever is left after dropping any scheme and path
  const host = trimmed
    .replace(/^dcaccount:/i, '')
    .replace(/^https?:\/\//i, '')
    .replace(/[/?#].*$/, '')
  if (host === '') {
    return undefined
  }
  if (/^dcaccount:/i.test(trimmed)) {
    return { qr: trimmed, host }
  }
  if (/^https?:\/\//i.test(trimmed)) {
    // keep the full URL (scheme, path, query) as the account-creation endpoint
    return { qr: `dcaccount:${trimmed}`, host }
  }
  return { qr: `dcaccount:${host}`, host }
}

/**
 * The `dcaccount:` QR the "create new account" flow should sign up with when
 * the operator set `SLOTHFUL_DEFAULT_CHATMAIL`, or `undefined` to fall back to
 * upstream's default instance.
 */
export function instanceDefaultChatmailQr(): string | undefined {
  return parseDefaultChatmail()?.qr
}

/**
 * Bare host of the operator-configured default chatmail relay, for the consent
 * text / privacy-policy link on the create-account flow. `undefined` when the
 * upstream default is in use.
 */
export function instanceDefaultChatmailHost(): string | undefined {
  return parseDefaultChatmail()?.host
}

/**
 * The anonymous-usage-statistics consent bridge installed by slothfulchat-web's
 * runtime.ts as `window.__slothfulAnalyticsUi`. Stats are opt-out: `enabled()`
 * reflects the current (localStorage-backed) choice, `setEnabled()` records a
 * new one, `showInfo()` opens the runtime's info dialog explaining what is
 * collected — its Opt out / Accept buttons record a choice too, so re-read
 * `enabled()` once the returned promise resolves (dialog closed).
 */
export type AnalyticsUi = {
  enabled: () => boolean
  setEnabled: (on: boolean) => void
  showInfo: () => Promise<void>
}

/**
 * The analytics consent bridge, or `undefined` when this instance was built
 * without analytics (every self-hosted build) or on upstream runtimes — so
 * callers simply hide their checkbox.
 */
export function instanceAnalyticsUi(): AnalyticsUi | undefined {
  const ui = (window as any).__slothfulAnalyticsUi
  return ui?.configured ? ui : undefined
}
